import { useState, useEffect } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { api } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { ApiError } from '@/types';

/**
 * SECURITY FIX: Secure Password Reset Flow
 *
 * Problem: Reset tokens in URLs can leak via:
 * - Referrer headers to third-party assets
 * - Browser history
 * - Screenshots/screen recordings
 * - Logs/analytics
 *
 * Solution: On page load, immediately exchange the URL token for a short-lived
 * session ID (10 min). The session ID is stored in memory only and used for
 * the actual password reset. The URL token becomes invalid after exchange.
 */

type TokenState = 'validating' | 'valid' | 'invalid' | 'expired';

export function ResetPasswordPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const urlToken = searchParams.get('token') || '';

  // Session ID from token exchange (stored in memory only, never in URL)
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [tokenState, setTokenState] = useState<TokenState>('validating');
  const [tokenError, setTokenError] = useState('');

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  // SECURITY: Exchange URL token for session ID immediately on mount
  useEffect(() => {
    if (!urlToken) {
      setTokenState('invalid');
      return;
    }

    const exchangeToken = async () => {
      try {
        const result = await api.validateResetToken(urlToken);
        setSessionId(result.session_id);
        setTokenState('valid');

        // SECURITY: Clear the token from the URL to prevent further leakage
        // Use replaceState to avoid adding to browser history
        window.history.replaceState({}, '', '/reset-password');
      } catch (err) {
        const apiError = err as ApiError;
        if (apiError.error?.includes('expired')) {
          setTokenState('expired');
          setTokenError('This reset link has expired. Please request a new one.');
        } else {
          setTokenState('invalid');
          setTokenError(apiError.error || 'Invalid reset link');
        }
      }
    };

    exchangeToken();
  }, [urlToken]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (password.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }

    if (!sessionId) {
      setError('Reset session has expired. Please request a new reset link.');
      return;
    }

    setIsLoading(true);

    try {
      // SECURITY: Use session ID, not the URL token
      await api.resetPassword(sessionId, password);
      setSuccess(true);
      setTimeout(() => navigate('/login'), 3000);
    } catch (err) {
      const apiError = err as ApiError;
      if (apiError.error?.includes('expired')) {
        setError('Reset session has expired. Please request a new reset link.');
      } else {
        setError(apiError.error || 'Failed to reset password');
      }
    } finally {
      setIsLoading(false);
    }
  };

  // Show loading while validating token
  if (tokenState === 'validating') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <CardTitle className="text-2xl">Validating Reset Link</CardTitle>
            <CardDescription>Please wait...</CardDescription>
          </CardHeader>
          <CardContent className="flex justify-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Show error for invalid/expired tokens
  if (tokenState === 'invalid' || tokenState === 'expired') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <CardTitle className="text-2xl">
              {tokenState === 'expired' ? 'Link Expired' : 'Invalid Reset Link'}
            </CardTitle>
            <CardDescription>
              {tokenError || 'This password reset link is invalid or has expired.'}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Link to="/forgot-password">
              <Button className="w-full">Request New Reset Link</Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
      <Card className="w-full max-w-md">
        <CardHeader className="space-y-4">
          <div className="flex justify-center">
            <img src="/Abby_Logo.png" alt="Abby Connect" className="h-16" />
          </div>
          <div className="text-center">
            <CardTitle className="text-2xl">Set New Password</CardTitle>
            <CardDescription>
              Enter your new password below
            </CardDescription>
          </div>
        </CardHeader>
        <CardContent>
          {success ? (
            <div className="space-y-4">
              <div className="rounded-lg bg-green-50 p-4 text-sm text-green-700">
                Your password has been reset successfully. Redirecting to login...
              </div>
              <Link to="/login">
                <Button variant="outline" className="w-full">
                  Go to Login
                </Button>
              </Link>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              {error && (
                <div className="rounded-lg bg-red-50 p-3 text-sm text-red-600">
                  {error}
                </div>
              )}
              <div className="space-y-2">
                <Label htmlFor="password">New Password</Label>
                <Input
                  id="password"
                  type="password"
                  placeholder="Minimum 8 characters"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  minLength={8}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="confirmPassword">Confirm Password</Label>
                <Input
                  id="confirmPassword"
                  type="password"
                  placeholder="Re-enter your password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                />
              </div>
              <Button type="submit" className="w-full" disabled={isLoading}>
                {isLoading ? 'Resetting...' : 'Reset Password'}
              </Button>
              <div className="text-center">
                <Link to="/login" className="text-sm text-primary hover:underline">
                  Back to Login
                </Link>
              </div>
            </form>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
